/*
 * Small key-value store for the device identity, the provisioning cache,
 * history, contacts and preferences: IndexedDB in browsers, a Map in tests.
 *
 * The device secret is a bearer credential: it has to be readable by the
 * page to be sent in Authorization headers, so WebCrypto non-extractable
 * keys do not help. It is protected by the origin's isolation only (any
 * script running on the origin can read it). docs/architecture.md lists the
 * trade-offs and the mitigations (strict CSP, no third-party scripts,
 * revocation from the portal, short-lived SIP credentials).
 */
export interface KV {
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  del(key: string): Promise<void>;
}

export class MemoryKV implements KV {
  readonly data = new Map<string, unknown>();
  async get<T>(key: string): Promise<T | undefined> {
    return structuredClone(this.data.get(key)) as T | undefined;
  }
  async set(key: string, value: unknown): Promise<void> {
    this.data.set(key, structuredClone(value));
  }
  async del(key: string): Promise<void> {
    this.data.delete(key);
  }
}

export class IndexedDbKV implements KV {
  private db: Promise<IDBDatabase>;

  constructor(name = "mtvox", private store = "kv") {
    this.db = new Promise((resolve, reject) => {
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(store);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private async tx<T>(mode: IDBTransactionMode, op: (s: IDBObjectStore) => IDBRequest): Promise<T> {
    const db = await this.db;
    return new Promise((resolve, reject) => {
      const req = op(db.transaction(this.store, mode).objectStore(this.store));
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => reject(req.error);
    });
  }

  get<T>(key: string): Promise<T | undefined> {
    return this.tx("readonly", (s) => s.get(key));
  }
  async set(key: string, value: unknown): Promise<void> {
    await this.tx("readwrite", (s) => s.put(value, key));
  }
  async del(key: string): Promise<void> {
    await this.tx("readwrite", (s) => s.delete(key));
  }
}

/** IndexedDB when it works (private windows may refuse it), else memory. */
export async function openKV(): Promise<KV> {
  try {
    const kv = new IndexedDbKV();
    await kv.get("probe");
    return kv;
  } catch {
    return new MemoryKV();
  }
}
